/*
 * PROD-7828 A-22 W6 — Payment emails: pure response shaping, plain-English status explanations, the
 * send-switch wording and error routing. Unit-tested in adapters.test.js.
 */
import { EM_DASH, api_error } from '../format';
import { reason_error } from '../needs_attention/params';

const str = (v) => (typeof v === 'string' ? v : '');
const list = (v) => (Array.isArray(v) ? v.map(str).filter(Boolean) : []);

export const CODE_SWITCH_CHANGED = 'EMAIL_SWITCH_CHANGED';
export const CODE_BAD_QUERY = 'BAD_OUTBOX_QUERY';

// teacher_payment_email_outbox.status, in the DDL's order (payemail.Statuses); labels as the server words them.
export const OUTBOX_STATUSES = [
  { value: 'PENDING', label: 'Waiting to send' },
  { value: 'SENDING', label: 'Sending' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'SKIPPED', label: 'Not sent' },
  { value: 'UNCONFIRMED', label: 'Could not confirm' }
];

const TONES = {
  PENDING: 'purple',
  SENDING: 'blue',
  SENT: 'green',
  FAILED: 'red',
  SKIPPED: 'gray',
  UNCONFIRMED: 'orange'
};

// What each status means for the teacher, in plain English (payemail/outcome.go).
export const status_help = (status, mode) => {
  switch (status) {
    case 'PENDING':
      return 'Queued. It is sent automatically, and retried automatically after a temporary problem.';
    case 'SENDING':
      return 'Being handed to the email service right now.';
    case 'SENT':
      return mode === 'TEST'
        ? 'Sent to the test inboxes only. The teacher did not get it.'
        : 'The email service accepted it for the teacher.';
    case 'FAILED':
      return 'Not sent. Every attempt failed, or a safety check stopped it.';
    case 'SKIPPED':
      return 'Never sent: the teacher could not be emailed (for example, no address or a deleted account).';
    case 'UNCONFIRMED':
      return 'It may or may not have reached the teacher. It is never resent automatically; check with the teacher first.';
    default:
      return '';
  }
};

const status_label = (status, mode, server_label) => {
  if (server_label) return server_label;
  const known = OUTBOX_STATUSES.find((s) => s.value === status);
  if (!known) return status || EM_DASH;
  return status === 'SENT' && mode === 'TEST' ? 'Sent (test)' : known.label;
};

// GET /email/outbox: newest first; next_before_id 0 = no more rows.
export const outbox_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => {
    const status = str(r.status);
    const mode = str(r.delivery_mode);
    return {
      id: typeof r.id === 'number' ? r.id : 0,
      uuid: str(r.uuid),
      kind_label: str(r.kind_label) || str(r.kind) || EM_DASH,
      teacher_uuid: str(r.teacher_uuid),
      teacher_name: str(r.teacher_name) || EM_DASH,
      pay_month: str(r.pay_month),
      status,
      status_label: status_label(status, mode, str(r.status_label)),
      status_tone: TONES[status] || 'gray',
      status_help: status_help(status, mode),
      is_test: mode === 'TEST',
      attempts: typeof r.attempts === 'number' ? r.attempts : 0,
      to: list(r.to),
      cc: list(r.cc),
      subject: str(r.subject),
      failure_reason: str(r.failure_reason),
      delivery_problem: str(r.delivery_problem),
      created_at: str(r.created_at_ist) || EM_DASH,
      next_attempt_at: str(r.next_attempt_at_ist),
      sent_at: str(r.sent_at_ist)
    };
  });
  const next =
    typeof d.next_before_id === 'number' && d.next_before_id > 0 ? d.next_before_id : null;
  return { rows, next_before_id: next };
};

const on_off = (on) => (on ? 'ON' : 'OFF');

// GET/POST /email/switch. null until a read lands (or when it failed).
export const email_switch_view = (data) => {
  if (!data || typeof data.live_enabled !== 'boolean') return null;
  const inboxes = list(data.test_inboxes);
  const test_to = inboxes.length ? inboxes.join(' and ') : 'the test inboxes';
  const live = str(data.delivery_mode) === 'LIVE';
  let effect;
  if (live) {
    effect = `Payment emails go to each teacher, with a copy to ${str(data.payments_inbox) || 'the payments inbox'}.`;
  } else if (data.live_ignored === true) {
    effect = `The switch is ON, but this is not production, so payment emails still go only to ${test_to}.`;
  } else {
    effect = `Payment emails go only to ${test_to}. Teachers get nothing.`;
  }
  const last = data.last_change;
  const changed =
    last && last.by_name
      ? `Turned ${on_off(last.to)} by ${last.by_name} on ${last.at_ist} IST. Reason: ${last.reason}`
      : '';
  return {
    enabled: data.live_enabled,
    live,
    can_toggle: data.can_toggle === true,
    label: `Send payment emails to teachers: ${on_off(data.live_enabled)}`,
    effect,
    last_change: changed
  };
};

export const switch_confirm_text = (target) =>
  target
    ? 'Turning this ON sends every new payment email to the teacher, with a copy to the payments inbox. Emails already sent to the test inboxes are not sent again.'
    : 'Turning this OFF sends every new payment email only to the test inboxes. Teachers get nothing until it is back ON.';

export const switch_confirm_label = (target) => `Turn sending to teachers ${on_off(target)}`;

export const switch_reason_error = reason_error;

// A failed switch POST, as the confirm panel shows it. reload: the switch moved meanwhile; re-read it.
export const switch_error = (e) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  if (code === CODE_SWITCH_CHANGED) {
    return {
      text: message || 'Sending to teachers was switched by someone else. Check it and try again.',
      reload: true
    };
  }
  if (status === 403) {
    return {
      text:
        message || 'Only a Teacher Payment super user can switch sending to teachers on or off.',
      reload: false
    };
  }
  return {
    text: message || 'Could not change the switch. Nothing was changed. Try again.',
    reload: false
  };
};

// A failed read: a 401/403 goes to the shell (no grant at all); anything else is shown in the tab.
export const read_error = (e, fallback) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  if (status === 401 || status === 403 || code === 'FORBIDDEN') return { kind: 'permission' };
  return { kind: 'inline', text: message || fallback };
};
